/**
 * RoleGate — renders children only when the current user's effective role
 * meets or exceeds the `min` threshold in the role hierarchy.
 *
 * FR-009: Both <RefinementsList> and <RefinementRun> wrap content in
 * <RoleGate min="Operator"> so users below Operator see a soft forbidden
 * empty state and no Convex query fires.
 *
 * Role hierarchy (PRD §7 / FR-015):
 *   operator (4) > builder (3) > reviewer (2) > viewer (1)
 *
 * While the role is loading (null), renders nothing to avoid flash-of-forbidden.
 */
import type { ReactNode } from "react";
import React from "react";
import { useEffectiveRole } from "../../api/hooks/useRBAC";

// ---------------------------------------------------------------------------
// Role hierarchy rank — higher number = more privileged
// ---------------------------------------------------------------------------
const ROLE_RANK: Record<string, number> = {
  operator: 4,
  builder: 3,
  reviewer: 2,
  viewer: 1,
};

interface RoleGateProps {
  /** Minimum role required to render children. */
  min: string;
  /** Content to render when the gate passes. */
  children: ReactNode;
  /** Content to render when the gate blocks. Defaults to null. */
  fallback?: ReactNode;
}

export default function RoleGate({
  min,
  children,
  fallback = null,
}: RoleGateProps): React.ReactElement | null {
  const effectiveRole = useEffectiveRole();

  // While role is loading, render nothing to avoid flash-of-forbidden.
  if (effectiveRole === null) {
    return null;
  }

  const userRank = ROLE_RANK[effectiveRole.toLowerCase()] ?? 0;
  const minRank = ROLE_RANK[min.toLowerCase()] ?? 0;

  if (userRank >= minRank) {
    return <>{children}</>;
  }

  return <>{fallback}</>;
}
